import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  BarChart3, 
  Flame, 
  Zap, 
  BookOpen, 
  Trophy,
  Calendar,
  Target,
  TrendingUp,
  ArrowLeft,
  Loader2
} from 'lucide-react';
import Layout from '../components/layout/Layout';
import { Progress } from '../components/ui/progress';
import { useApp } from '../context/AppContext';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { 
  AreaChart, 
  Area, 
  XAxis, 
  YAxis, 
  ResponsiveContainer,
  BarChart,
  Bar,
  Tooltip
} from 'recharts';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Helper function to get auth token from storage
const getAuthToken = () => {
  try {
    return localStorage.getItem('fuse_auth_token') || sessionStorage.getItem('fuse_auth_token');
  } catch (e) {
    return null;
  }
};

const StatsPage = () => {
  const navigate = useNavigate();
  const { userProgress, tracks, achievements } = useApp();
  const { user } = useAuth();
  const [dbStats, setDbStats] = useState(null);
  const [loading, setLoading] = useState(true);

  // Fetch stats from database
  useEffect(() => {
    const fetchStats = async () => {
      try {
        const token = getAuthToken();
        if (token) {
          const response = await axios.get(`${API_URL}/api/progress/stats`, {
            withCredentials: true,
            headers: {
              'Authorization': `Bearer ${token}`
            }
          });
          setDbStats(response.data);
        }
      } catch (error) {
        console.error('Failed to fetch stats from database:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchStats();
  }, []);

  // Use database stats if available, fallback to local
  const totalXP = dbStats?.total_xp ?? user?.total_xp ?? userProgress.totalXP;
  const lessonsCompleted = dbStats?.lessons_completed ?? userProgress.lessonsCompleted;
  const currentStreak = dbStats?.current_streak ?? userProgress.currentStreak;
  const level = dbStats?.level ?? Math.floor(totalXP / 500) + 1;

  // Calculate stats
  const totalLessonsInTracks = tracks.reduce((sum, t) => sum + t.totalLessons, 0);
  const completedLessonsInTracks = tracks.reduce((sum, t) => sum + t.completedLessons, 0);
  const overallProgress = (completedLessonsInTracks / totalLessonsInTracks) * 100;
  
  const earnedAchievements = achievements.filter(a => a.earned).length;
  const levelProgress = (totalXP % 500) / 500 * 100;
  const xpToNextLevel = 500 - (totalXP % 500);

  // Mock weekly XP data
  const weeklyData = [
    { day: 'Seg', xp: 120 },
    { day: 'Ter', xp: 80 },
    { day: 'Qua', xp: 150 },
    { day: 'Qui', xp: 90 },
    { day: 'Sex', xp: 200 },
    { day: 'Sáb', xp: 60 },
    { day: 'Dom', xp: 100 },
  ];

  // Mock monthly progress
  const monthlyData = [
    { week: 'Sem 1', lessons: 5 },
    { week: 'Sem 2', lessons: 8 },
    { week: 'Sem 3', lessons: 6 },
    { week: 'Sem 4', lessons: 10 },
  ];

  // Study calendar (last 28 days)
  const generateCalendar = () => {
    const days = [];
    const today = new Date();
    for (let i = 27; i >= 0; i--) {
      const date = new Date(today);
      date.setDate(date.getDate() - i);
      const studied = Math.random() > 0.3; // Mock data
      days.push({
        date: date.getDate(),
        studied,
        isToday: i === 0
      });
    }
    return days;
  };

  const calendarDays = generateCalendar();

  if (loading) {
    return (
      <Layout showBack title="Estatísticas">
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout showBack title="Estatísticas">
      <div className="px-4 py-6 space-y-6">
        {/* Header Stats */}
        <div className="grid grid-cols-2 gap-3">
          <div className="card-elevated p-4 text-center">
            <div className="w-12 h-12 rounded-xl bg-accent/10 flex items-center justify-center mx-auto mb-2">
              <Flame className="w-6 h-6 text-accent" />
            </div>
            <p className="text-2xl font-bold">{currentStreak}</p>
            <p className="text-xs text-muted-foreground">Dias Seguidos</p>
          </div>
          
          <div className="card-elevated p-4 text-center">
            <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center mx-auto mb-2">
              <Zap className="w-6 h-6 text-primary" />
            </div>
            <p className="text-2xl font-bold">{totalXP}</p>
            <p className="text-xs text-muted-foreground">XP Total</p>
          </div>
          
          <div className="card-elevated p-4 text-center">
            <div className="w-12 h-12 rounded-xl bg-success/10 flex items-center justify-center mx-auto mb-2">
              <BookOpen className="w-6 h-6 text-success" />
            </div>
            <p className="text-2xl font-bold">{lessonsCompleted}</p>
            <p className="text-xs text-muted-foreground">Lições Completas</p>
          </div>
          
          <div className="card-elevated p-4 text-center">
            <div className="w-12 h-12 rounded-xl bg-warning/10 flex items-center justify-center mx-auto mb-2">
              <Trophy className="w-6 h-6 text-warning" />
            </div>
            <p className="text-2xl font-bold">{earnedAchievements}</p>
            <p className="text-xs text-muted-foreground">Conquistas</p>
          </div>
        </div>

        {/* Level Progress */}
        <div className="card-elevated p-5 space-y-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-xl bg-primary flex items-center justify-center">
                <span className="text-primary-foreground font-bold text-lg">{level}</span>
              </div>
              <div>
                <h3 className="font-semibold">Nível {level}</h3>
                <p className="text-sm text-muted-foreground">{xpToNextLevel} XP para nível {level + 1}</p>
              </div>
            </div>
            <TrendingUp className="w-5 h-5 text-primary" />
          </div>
          <Progress value={levelProgress} className="h-3" />
        </div>

        {/* Weekly XP Chart */}
        <div className="card-elevated p-5 space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="font-semibold flex items-center gap-2">
              <BarChart3 className="w-5 h-5 text-primary" />
              XP da Semana
            </h3>
            <span className="text-sm text-muted-foreground">
              Total: {weeklyData.reduce((sum, d) => sum + d.xp, 0)} XP
            </span>
          </div>
          <div className="h-40">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={weeklyData}>
                <XAxis 
                  dataKey="day" 
                  axisLine={false}
                  tickLine={false}
                  tick={{ fontSize: 12, fill: 'hsl(var(--muted-foreground))' }}
                />
                <YAxis hide />
                <Tooltip 
                  contentStyle={{ 
                    background: 'hsl(var(--card))', 
                    border: '1px solid hsl(var(--border))',
                    borderRadius: '8px'
                  }}
                  formatter={(value) => [`${value} XP`, 'XP']}
                />
                <Bar 
                  dataKey="xp" 
                  fill="hsl(var(--primary))" 
                  radius={[4, 4, 0, 0]}
                />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Study Calendar */}
        <div className="card-elevated p-5 space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="font-semibold flex items-center gap-2">
              <Calendar className="w-5 h-5 text-success" />
              Calendário de Estudos
            </h3>
            <span className="text-sm text-muted-foreground">
              Últimos 28 dias
            </span>
          </div>
          <div className="grid grid-cols-7 gap-1.5">
            {['D', 'S', 'T', 'Q', 'Q', 'S', 'S'].map((day, i) => (
              <div key={i} className="text-center text-xs text-muted-foreground font-medium py-1">
                {day}
              </div>
            ))}
            {calendarDays.map((day, index) => (
              <div
                key={index}
                className={`aspect-square rounded-md flex items-center justify-center text-xs font-medium transition-colors ${
                  day.isToday 
                    ? 'ring-2 ring-primary ring-offset-2' 
                    : ''
                } ${
                  day.studied 
                    ? 'bg-success text-success-foreground' 
                    : 'bg-muted text-muted-foreground'
                }`}
              >
                {day.date}
              </div>
            ))}
          </div>
          <p className="text-xs text-muted-foreground text-center">
            🟢 Dias estudados • ⚪ Dias sem estudo
          </p>
        </div>

        {/* Track Progress */}
        <div className="card-elevated p-5 space-y-4">
          <h3 className="font-semibold flex items-center gap-2">
            <Target className="w-5 h-5 text-accent" />
            Progresso por Trilha
          </h3>
          <div className="space-y-3">
            {tracks.map(track => {
              const progress = (track.completedLessons / track.totalLessons) * 100;
              return (
                <div key={track.id} className="space-y-1.5">
                  <div className="flex items-center justify-between text-sm">
                    <span className="flex items-center gap-2">
                      <span>{track.icon}</span>
                      <span>{track.titlePt}</span>
                    </span>
                    <span className="font-medium">{Math.round(progress)}%</span>
                  </div>
                  <Progress value={progress} className="h-2" />
                </div>
              );
            })}
          </div>
        </div>

        {/* Overall Progress */}
        <div className="p-4 rounded-xl bg-gradient-to-r from-primary/10 to-accent/10 text-center">
          <p className="text-sm text-muted-foreground mb-1">Progresso Geral do Curso</p>
          <p className="text-3xl font-bold gradient-text">{Math.round(overallProgress)}%</p>
          <p className="text-xs text-muted-foreground mt-1">
            {completedLessonsInTracks} de {totalLessonsInTracks} lições completas
          </p>
        </div>
      </div>
    </Layout>
  );
};

export default StatsPage;
